import cn from "classnames";
import {
	SCREENER_COLUMNS,
	type ScreenerColumn,
	type ScreenerSubField,
} from "epsm-api-types/models";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMosaic } from "@/context/mosaic/MosaicContext";
import type { ViewState } from "@/context/WidgetViewContext";
import { useWidgetView } from "@/context/WidgetViewContext";
import type { Result } from "@/interfaces/Result";
import { isEnabledScorer } from "@/lib/utils/features";
import sprites from "/src/shared/assets/sprite.svg";
import { scorerPrefix } from "../widgets/Screener/default";
import { generateGridCols } from "../widgets/Screener/ScreenerFilters/helpers/generateGridCols";

type ColumnsProps = {
	width?: number;
	baseId: string;
	results?: Result[];
	isScorerOpened?: boolean;
};

export const getFirstSubfield = (
	subFields: ScreenerSubField[],
): ScreenerSubField[] => {
	if (!subFields.length) return [];

	const [firstSubfield] = subFields;

	if (firstSubfield.subFields?.length) {
		return [
			{
				...firstSubfield,
				subFields: getFirstSubfield(firstSubfield.subFields),
			},
		];
	}

	return [firstSubfield];
};

export const Columns: React.FC<ColumnsProps> = ({
	width,
	baseId,
	results,
	isScorerOpened,
}) => {
	const { isMonitor } = useMosaic();
	const {
		visibleColumns,
		setVisibleColumns,
		scorerColumns,
		setScorerColumns,
		selectedColumn,
		setSelectedColumn,
		INITIAL_GROUP,
		selectedGroup,
		isShowDescription,
		setIsShowDescription,
		setIsRelative,
	} = useWidgetView();

	const [query, setQuery] = useState("");

	const isScorerEnabled = isEnabledScorer();

	const minWidthForLargeDefinitionBtn = 855;

	const matchesGroup = (column: ScreenerColumn) => {
		return (
			selectedGroup[baseId] === INITIAL_GROUP ||
			column.groupName === selectedGroup[baseId]
		);
	};

	const normalizedQuery = query.toLowerCase().trim();

	const filteredColumns = SCREENER_COLUMNS.filter(
		(column) =>
			matchesGroup(column) &&
			(column.title.toLowerCase().trim().includes(normalizedQuery) ||
				column.titleShort?.toLowerCase().trim().includes(normalizedQuery) ||
				column.titleAbbreviation
					?.toLowerCase()
					.trim()
					.includes(normalizedQuery)),
	);

	const toggleColumnVisibility = (
		baseId: string,
		column: ScreenerColumn,
		setVisibleColumns: React.Dispatch<
			React.SetStateAction<ViewState<ScreenerColumn[]>>
		>,
	) => {
		const shouldAddToScorer =
			isScorerEnabled &&
			column.type === "num" &&
			isScorerOpened &&
			!scorerColumns.some((col) => col.name.includes(column.name)) &&
			scorerColumns.length < 6;

		if (shouldAddToScorer) {
			const newColumn = {
				...column,
				name: `${scorerPrefix}${column.name}`,
				...(column.subFields?.length && {
					subFields: getFirstSubfield(column.subFields),
				}),
			};

			setScorerColumns((prev) => [...prev, newColumn]);
		}

		setVisibleColumns((prevVisibleColumns) => {
			const isColumnVisible = prevVisibleColumns[baseId]?.some(
				(visibleColumn) => visibleColumn.name === column.name,
			);

			return {
				...prevVisibleColumns,
				[baseId]: isColumnVisible
					? prevVisibleColumns[baseId].filter(
							(visibleColumn) => visibleColumn.name !== column.name,
						)
					: [
							...prevVisibleColumns[baseId],
							{
								...column,
								...(column.subFields?.length && {
									subFields: getFirstSubfield(column.subFields),
								}),
							},
						],
			};
		});
	};

	const columnNames = visibleColumns[baseId].map((item) => item.name);

	return (
		<div className="relative min-w-40 w-full border-box rounded-sm">
			<div className="flex justify-between px-1.5 py-1">
				{width && width > 1300 && (
					<p className="text-accent-foreground italic pt-px">Fields</p>
				)}

				<div
					className={cn("flex gap-1 justify-between w-full", {
						"pb-1 flex-col-reverse": width && width < 1301,
					})}
				>
					<div
						className={cn("relative w-full ml-auto", {
							"max-w-60": width && width > 1300,
						})}
					>
						<svg
							aria-label="search"
							role="img"
							fill="none"
							stroke="var(--light-gray)"
							className="w-2.5 h-2.5 absolute top-[4px] ml-1.5"
						>
							<use xlinkHref={`${sprites}#search`} />
						</svg>

						<Input
							className="bg-background pl-5"
							type="text"
							placeholder="Search field..."
							value={query}
							onChange={(event) => setQuery(event.target.value)}
						/>
					</div>

					<div className="flex gap-5">
						{width && width < 1301 && (
							<p className="text-accent-foreground italic pt-px">Fields</p>
						)}

						<div className="flex gap-1 min-w-0 ml-auto">
							<Button
								className={cn(
									"h-[18px]",
									width && width > minWidthForLargeDefinitionBtn
										? "px-4"
										: "px-2",
								)}
								variant="outline"
								disabled={!selectedColumn[baseId]?.description}
								onClick={() =>
									setIsShowDescription({
										...isShowDescription,
										[baseId]: !isShowDescription[baseId],
									})
								}
							>
								<svg
									aria-label="info"
									role="img"
									fill="none"
									stroke="var(--light-gray)"
									className="shrink-0 w-3 h-3 group-hover:stroke-accent-foreground"
								>
									<use xlinkHref={`${sprites}#info`} />
								</svg>

								<p
									className={cn(
										`w-full pl-1 pr-[1px] text-left text-ellipsis
									overflow-hidden whitespace-nowrap group-hover:text-accent-foreground`,
										{ "text-accent-foreground": isShowDescription[baseId] },
									)}
								>
									Definition
								</p>
							</Button>
						</div>
					</div>
				</div>
			</div>

			<div
				className={cn("grid gap-0.5 px-0.5 pb-1 overflow-y-scroll max-h-base", {
					"max-h-fields-info": isShowDescription[baseId],
					"max-h-fields-tablet-info":
						isShowDescription[baseId] && width && width < 1301,
					"max-h-fields-tablet-base":
						!isShowDescription[baseId] && width && width < 1301,
				})}
				style={
					width
						? generateGridCols(
								width,
								isMonitor(baseId) ? 200 : 300,
								isMonitor(baseId) ? 20 : 15,
								isMonitor(baseId),
							)
						: {}
				}
			>
				{filteredColumns.length !== 0 ? (
					filteredColumns.map((column: ScreenerColumn) => (
						<Button
							key={column.name}
							className={cn(
								`inline-block h-max py-0 px-1.5 text-stone-400 
								text-start hover:text-accent-foreground`,
								isMonitor(baseId)
									? {
											"text-gold": columnNames.includes(column.name),
											"bg-background":
												column.name === selectedColumn[baseId]?.name,
										}
									: {
											"text-accent-foreground": columnNames.includes(
												column.name,
											),
											"text-gold bg-primary/90":
												column.title === selectedColumn[baseId]?.title &&
												results?.some((res) =>
													res.result.some(
														(result) =>
															"name" in result && result.name === column.name,
													),
												),
											"text-gold":
												results?.some((res) =>
													res.result.some(
														(result) =>
															"name" in result && result.name === column.name,
													),
												) && column.title !== selectedColumn[baseId]?.title,
											"bg-background":
												column.title === selectedColumn[baseId]?.title,
										},
							)}
							variant="filter_column"
							onClick={() => {
								toggleColumnVisibility(baseId, column, setVisibleColumns);

								if (selectedColumn[baseId]?.name === column.name) {
									setSelectedColumn({ ...selectedColumn, [baseId]: null });
								} else {
									setSelectedColumn({ ...selectedColumn, [baseId]: column });
								}

								setIsRelative(false);
							}}
						>
							{column.titleShort
								? column.titleShort.slice(0, 20)
								: column.title.slice(0, 20)}
						</Button>
					))
				) : (
					<p
						className="
							text-accent-foreground absolute top-1/2 left-1/2 transform 
							-translate-x-1/2 -translate-y-1/2"
					>
						{SCREENER_COLUMNS.find((column) => matchesGroup(column))
							? "No match for this input"
							: "No fields for this category"}
					</p>
				)}
			</div>

			{isShowDescription[baseId] && (
				<div className="absolute bottom-0 w-full h-20 border-t border-primary">
					<div className="text-white/80 px-4 py-1 h-full overflow-y-scroll">
						<p className="text-gold">{selectedColumn[baseId]?.title}:</p>
						<p>{selectedColumn[baseId]?.description}</p>
					</div>
				</div>
			)}
		</div>
	);
};
